/*
    FormFieldText.tsx - Component for text fields within an HTML form.
*/

import type { JSX } from 'react';
import { useState } from 'react';
import { FormControl, FormGroup, InputGroup } from 'react-bootstrap';

import { IconButton } from 'Components/Buttons/IconButton';
import { Colors } from 'Components/Colors';
import styles from 'Components/FormField/FormField.module.css';
import { FormLabel } from 'Components/FormField/FormLabel/FormLabel';
import { FormFieldTooltip } from 'Components/Tooltips/FormFieldTooltip';
import { ICON_VIEW, ICON_VIEW_HIDDEN } from 'Config/Icons';

interface FormFieldTextProps {
    color?: Colors;
    formFieldId: string;
    formFieldLabel?: string;
    formFieldType?: string;
    required?: boolean;
    hideRequiredSymbol?: boolean;
    tooltip?: string;
    placeholder?: string;
    pattern?: string;
    handleChange?: any;
    value?: string | number;
    disabled?: boolean;
    max?: number;
    step?: number;
    min?: number;
    onInput?: (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    ariaLabel?: string;
}

export const FormFieldText = (props: FormFieldTextProps): JSX.Element => {
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const isPasswordField = props.formFieldType === 'password';
    const effectiveFieldType = isPasswordField && showPassword ? 'text' : props.formFieldType;

    return (
        <FormGroup controlId={props.formFieldId} className={styles.formField}>
            {props.formFieldLabel && (
                <FormLabel color={props.color} required={props.required} hideRequiredSymbol={props.hideRequiredSymbol}>
                    {props.formFieldLabel}
                </FormLabel>
            )}
            {props.tooltip && <FormFieldTooltip text={props.tooltip} />}
            <InputGroup>
                <FormControl name={props.formFieldId} className={styles.customFormControl} type={effectiveFieldType} required={props.required} placeholder={props.placeholder} pattern={props.pattern} onChange={props.handleChange} value={props.value} disabled={props.disabled} onInput={props.onInput} max={props.max} min={props.min} step={props.step} aria-label={props.ariaLabel} />
                {isPasswordField && (
                    <InputGroup.Text>
                        <div className={styles.passwordToggleVisibilityButton}>
                            <IconButton onClick={() => setShowPassword(!showPassword)} fontAwesomeImage={showPassword ? ICON_VIEW : ICON_VIEW_HIDDEN} aria-label={showPassword ? 'Hide password' : 'Show password'} />
                        </div>
                    </InputGroup.Text>
                )}
            </InputGroup>
        </FormGroup>
    );
};
